import { useEffect, useRef, useState, useCallback } from "react"
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  TextField,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import PersonSearchIcon from "@mui/icons-material/PersonSearch"
import NoAccountsIcon from "@mui/icons-material/NoAccounts"
import useTranslationSharie from "../hooks/Language/useTranslation"
import { useApiService } from "../hooks/Api/useApiService"
import { useSocket } from "../hooks/Utils/Socket/SocketManager"
import useDeviceType from "../hooks/Utils/useDeviceType"
// אם יש לך useError:
import useError from "../hooks/Error/useError"
import T from "../hooks/Language/T"
import MailOutlineIcon from "@mui/icons-material/MailOutline"

const BRAND = {
  purple: "#6a11cb",
  purpleDark: "#4b0e99",
  blue: "#2575fc",
  blueDark: "#1b5ac2",
  paper: "#ffffff",
  bgSoft: "#f5f7fa",
}

export default function Messanging() {
  const [selectedUser, setSelectedUser] = useState(null)
  const [messages, setMessages] = useState([])
  const [peers, setPeers] = useState([])
  const [images, setImages] = useState([])
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const t = useTranslationSharie()
  const device = useDeviceType()
  const { postData } = useApiService()
  const socket = useSocket()
  const { Error: AlertError } = useError?.() || { Error: () => null }

  const me = localStorage.getItem("user")
  const chatRef = useRef(null)

  const scrollToBottom = useCallback(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" })
  }, [])

  /** ----- SOCKET LISTENERS ----- */
  useEffect(() => {
    if (!socket) return
    const onReceive = (data) => {
      setMessages((prev) => [
        ...prev,
        {
          from: data.from,
          to: data.to,
          message: data.message,
          timestamp: new Date(),
          seen_to: false,
        },
      ])
    }
    socket?.on("receive_message", onReceive)
    return () => socket?.off("receive_message", onReceive)
  }, [socket])

  /** ----- FETCH INITIAL DATA ----- */
  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setLoading(true)
        const data = await postData("sharie/getmessages", { to: me })
        const mapped = (data?.messages || [])
          .map((m) => ({ ...m, timestamp: new Date(m.timestamp) }))
          .sort((a, b) => a.timestamp - b.timestamp)
        if (!mounted) return
        setMessages(mapped)

        // חישוב רשימת בני שיח ייחודיים
        const uniq = []
        mapped.forEach((m) => {
          if (m.from !== me && !uniq.includes(m.from)) uniq.push(m.from)
          if (m.to !== me && !uniq.includes(m.to)) uniq.push(m.to)
        })
        setPeers(uniq)

        // תמונות (אם קיימות)
        if (uniq.length) {
          const imgs = await postData("users/getusersimages", { users: uniq })
          if (!mounted) return
          setImages(imgs?.images || [])
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** ----- SCROLL ON NEW MESSAGES ----- */
  useEffect(() => {
    scrollToBottom()
  }, [messages, selectedUser, scrollToBottom])

  /** ----- MARK SEEN WHEN OPENING A CONVERSATION ----- */
  useEffect(() => {
    const inThread = messages.filter((m) => m.from === selectedUser || m.to === selectedUser)
    if (!inThread.length) return
    postData("sharie/markmessagesseen", {
      messages: inThread,
      user_name: me,
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser])

  /** ----- COMPUTE CURRENT THREAD ----- */
  const thread = selectedUser
    ? messages.filter((m) => m.from === selectedUser || m.to === selectedUser)
    : []
  console.log(thread)
  /** ----- SEND MESSAGE ----- */
  const sendMessage = async () => {
    const text = input.trim()
    if (!text || !selectedUser) return
    setSending(true)
    const msg = {
      from: me,
      to: selectedUser,
      message: text,
      timestamp: new Date(),
      seen_to: false,
    }

    try {
      // Emit live
      socket?.emit("send_message", { toUserId: selectedUser, message: msg })
      socket?.emit("send_notification", {
        toUserId: selectedUser,
        type: "message",
        from: me,
        to: selectedUser,
      })

      // Persist
      await postData("sharie/addmessage", msg)

      // Update UI optimistically
      setMessages((prev) => [...prev, msg])
      setInput("")
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      setSending(false)
    }
  }

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    } else if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  /** ----- LAYOUT ----- */
  // גודל גדול ואלגנטי – מתאים לכל שטח המודל
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "0.8rem",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: 8,
        background: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.blue} 100%)`,
      }}
    >
      {/* HEADER */}
      <T>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "text.primary",
            background: "transparent",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ChatBubbleOutlineIcon />
            <Typography variant='h6' fontWeight={700}>
              {t("Messages")}
            </Typography>
            <Typography variant='body2' sx={{ opacity: 0.85 }}>
              {selectedUser ? `Chat with ${selectedUser}` : t("Select a conversation")}
            </Typography>
          </Box>

          {selectedUser && (
            <Tooltip title='Back to conversations'>
              <IconButton onClick={() => setSelectedUser(null)} sx={{ color: "text.primary" }}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </T>
      {/* BODY */}
      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: 0,
          backgroundColor: BRAND.bgSoft,
        }}
      >
        {/* LEFT: CONVERSATIONS LIST */}
        <Box
          sx={{
            backgroundColor: BRAND.paper,
            borderRight: { md: "1px solid rgba(0,0,0,0.08)" },
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
            <Typography variant='subtitle2' color='text.secondary'>
              {t("Conversations")}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto" }}>
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : peers.length ? (
              <List disablePadding>
                {peers.map((user, i) => (
                  <Box key={`${user}-${i}`}>
                    <ListItemButton
                      selected={selectedUser === user}
                      onClick={() => setSelectedUser(user)}
                      sx={{
                        py: 1.2,
                        "&.Mui-selected": {
                          backgroundColor: "rgba(106,17,203,0.08)",
                          "&:hover": { backgroundColor: "rgba(106,17,203,0.12)" },
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar src={images?.[i]} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={user}
                        secondary={selectedUser === user ? t("Open") : t("Tap to open")}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItemButton>
                    <Divider />
                  </Box>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  px: 3,
                  color: "text.secondary",
                }}
              >
                <PersonSearchIcon fontSize='large' />
                <Typography variant='subtitle1' fontWeight={600} sx={{ mt: 1 }}>
                  {t("No conversations yet")}
                </Typography>
                <Typography variant='body2'>
                  {t("Start by sending a message to a user.")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* RIGHT: CHAT THREAD */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            backgroundColor: BRAND.bgSoft,
          }}
        >
          {/* Thread header */}
          <T>
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                background:
                  "linear-gradient(135deg, rgba(106,17,203,0.9) 0%, rgba(37,117,252,0.9) 100%)",
                color: "text.primary",
              }}
            >
              {selectedUser ? (
                <>
                  <Avatar src={images?.[peers.findIndex((p) => p === selectedUser)] || undefined} />
                  <Box>
                    <Typography variant='subtitle1' fontWeight={700}>
                      {selectedUser}
                    </Typography>
                    <Typography variant='caption' sx={{ opacity: 0.9 }}>
                      {t("Online recently")}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <NoAccountsIcon />
                  <Typography variant='subtitle1' fontWeight={700}>
                    {t("Choose a conversation to start chatting")}
                  </Typography>
                </>
              )}
            </Box>
          </T>
          {/* Messages list */}
          <Box
            ref={chatRef}
            sx={{
              flex: 1,
              overflow: "auto",
              px: { xs: 1.5, md: 2.5 },
              py: 2,
            }}
          >
            {selectedUser ? (
              thread.length ? (
                thread.map((m, idx) => {
                  const mine = m.from === me
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        justifyContent: mine ? "flex-end" : "flex-start",
                        mb: 2,
                        px: 1,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "70%",
                          px: 2,
                          py: 1.5,
                          borderRadius: mine ? "1rem 1rem 0.2rem 1rem" : "1rem 1rem 1rem 0.2rem",
                          background: mine
                            ? "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)"
                            : "#ffffff",
                          color: mine ? "white" : "text.primary",
                          boxShadow: mine ? 3 : 1,
                          fontSize: "1rem",
                          lineHeight: 1.6,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.02)",
                          },
                        }}
                      >
                        <Typography variant='body1'>{m.message}</Typography>
                        <Typography
                          variant='caption'
                          sx={{
                            opacity: 0.7,
                            display: "block",
                            mt: 0.8,
                            fontSize: "0.75rem",
                            textAlign: mine ? "right" : "left",
                          }}
                        >
                          {new Date(m.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 6,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant='body2'>{t("No messages yet. Say hi")}</Typography>
                </Box>
              )
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  px: 3,
                  color: "text.secondary",
                }}
              >
                <Typography variant='body1'>
                  {t("Pick a user from the left to start a conversation.")}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Composer */}
          <T>
            <Box
              sx={{
                p: 1.25,
                borderTop: "1px solid rgba(0,0,0,0.06)",
                backgroundColor: BRAND.paper,
                display: "flex",
                alignItems: "flex-end",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={6}
                placeholder={selectedUser ? t("Type a message…") : t("Select a conversation first")}
                disabled={!selectedUser || sending}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (!selectedUser) return
                  if ((e.key === "Enter" && !e.shiftKey) || (e.key === "Enter" && e.ctrlKey)) {
                    e.preventDefault()
                    sendMessage()
                  }
                }}
              />
              <Tooltip title='Send (Enter)'>
                <span>
                  <IconButton
                    onClick={sendMessage}
                    disabled={!selectedUser || !input.trim() || sending}
                    sx={{
                      bgcolor: BRAND.purple,
                      color: "text.primary",
                      "&:hover": { bgcolor: BRAND.purpleDark },
                      width: 44,
                      height: 44,
                      borderRadius: "0.8rem",
                    }}
                  >
                    {sending ? <CircularProgress size={22} color='inherit' /> : <MailOutlineIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </T>
        </Box>
      </Box>

      {/* FOOTER STRIP */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "text.primary",
          background: "linear-gradient(135deg, rgba(106,17,203,0.9) 0%, rgba(37,117,252,0.9) 100%)",
        }}
      >
        <Typography variant='caption' sx={{ opacity: 0.9 }}>
          Powered by Sharie ✨
        </Typography>
        <Button
          size='small'
          variant='outlined'
          sx={{
            color: "text.primary",
            borderColor: "rgba(255,255,255,0.8)",
            textTransform: "none",
            "&:hover": { borderColor: "#fff" },
          }}
          onClick={() => setSelectedUser(null)}
        >
          {t("Conversations")}
        </Button>
      </Box>

      <AlertError />
    </Box>
  )
}
